import { useLocalization } from '@components/Localization'
import LocalizedLink from '@components/Localization/LocalizedLink'
import { FloatingServiceMenu, Footer, Header, Page, PageTransition } from '@components/Page'
import CartSummary from '@components/_cart/CartSummary'
import EmptyCart from '@components/_cart/EmptyCart'
import CartList from '@components/_cart/EmptyCart/CartList'
import StickySummary from '@components/_cart/StickySummary'
import { Box, breakpoints, CheckoutLink, Container, Flex, Grid, Heading, Text } from '@gassan-ui'
import { useUpdateCartItemQuantityMutation, CartItem } from '@generated'
import { formatPrice } from '@lib/formatPrice'

import { useBreakpoint } from '@lib/hooks/useBreakpoint'
import { AnimatePresence } from 'framer-motion'
import { NextPage } from 'next'
import React, { useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useCart } from '@lib/hooks/useCart'

const Cart: NextPage = () => {
  const cart = useCart()
  const { lang } = useLocalization()
  const [gridRef, gridInView] = useInView()
  const [summaryRef, summaryInView] = useInView()
  const [loadingRow, setLoadingRow] = useState<string | null>(null)
  const [isRendered, setRendered] = useState(false)

  const isDesktop = useBreakpoint(breakpoints.large)
  const total = cart ? cart.totalItems : 0

  const [, updateCartItemQuantity] = useUpdateCartItemQuantityMutation()

  const handleChangeQuantity = async (cartItemId: string, quantity: number) => {
    setLoadingRow(cartItemId)
    try {
      await updateCartItemQuantity({ reference: cart.id, cartItemId, quantity })
      setLoadingRow(null)
    } catch (error) {
      // Show error notification
      setLoadingRow(null)
    }
  }

  useEffect(() => {
    setRendered(true)
  }, [])

  return (
    <Page
      title={lang === 'nl' ? 'Winkelmand' : 'Shopping cart'}
      slug={lang === 'nl' ? 'winkelmand' : 'cart'}
      altSlug={lang === 'nl' ? 'cart' : 'winkelmand'}
    >
      <FloatingServiceMenu
        moveElementUp={!summaryInView && gridInView && isRendered && !isDesktop && '3.5rem'}
      />
      <Header initialVariant="absolute" />
      <PageTransition>
        <Container>
          <Box
            pt={{ _: '1rem', small: '1.5rem', large: '2.5rem', xlarge: '3.5rem' }}
            pb={{ _: '1.75rem', small: '1.5rem', large: '2rem' }}
          >
            <LocalizedLink href="/">
              <CheckoutLink
                href="/"
                title={lang === 'nl' ? 'Verder winkelen' : 'Continue shopping'}
              />
            </LocalizedLink>
            <Flex
              mt={{ _: '0.5rem', small: '2rem', large: '2.5rem' }}
              justifyContent="space-between"
              alignItems="center"
            >
              <Heading variant="h1" as="h1" mb="0">
                {lang === 'nl' ? 'Uw winkelwagen' : 'Your shopping cart'}
              </Heading>
              <Box display={['none', 'contents']}>
                {cart && (
                  <Text variant="large" color="shades.500" mb="0">
                    {`${total} ${
                      lang === 'nl'
                        ? total === 1
                          ? 'artikel'
                          : 'artikelen'
                        : total === 1
                        ? 'article'
                        : 'articles'
                    }`}
                  </Text>
                )}
              </Box>
            </Flex>
          </Box>
          {!cart ? (
            <Box minHeight={500}></Box>
          ) : cart?.items.length === 0 ? (
            <EmptyCart />
          ) : (
            <>
              <Grid
                ref={gridRef}
                gridTemplateColumns={{ _: '1fr', large: '1fr 28rem' }}
                gridColumnGap={{ _: '2rem', xxlarge: '5rem' }}
                alignItems="flex-start"
              >
                <CartList
                  items={cart.items as CartItem[]}
                  loadingRow={loadingRow}
                  handleChangeQuantity={(cartItemId, quantity) =>
                    handleChangeQuantity(cartItemId, quantity)
                  }
                />
                <Box ref={summaryRef} suppressHydrationWarning>
                  <CartSummary status={loadingRow ? 'updating' : 'idle'} />
                </Box>
              </Grid>
              <Box display={{ _: ' none', large: 'block' }} mt="2.5rem">
                <LocalizedLink href="/">
                  <CheckoutLink title={lang === 'nl' ? 'Verder winkelen' : 'Continue shopping'} />
                </LocalizedLink>
              </Box>
              <AnimatePresence>
                {!summaryInView && gridInView && isRendered && !isDesktop && (
                  <StickySummary
                    price={formatPrice(cart.totalPriceIncTax, true)}
                    status={loadingRow ? 'updating' : 'idle'}
                  />
                )}
              </AnimatePresence>
            </>
          )}
        </Container>
        <Footer />
      </PageTransition>
    </Page>
  )
}

export default Cart
